import React, { useEffect, useState } from "react";
import PlacesAutocomplete,
{ geocodeByAddress,
geocodeByPlaceId } from "react-places-autocomplete";
import { toast } from "react-toastify";
import './autocomplete.scss';
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';


export default function GooglePlacesAutocomplete({
    defaultValue,
    country,
    disabled,
    onChange,
    onSelectResult,
    clearErrors,
    errors
}) {
    const { t } = useTranslation()
    const [selected, setSelected] = useState('');
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const [hasConfirmedSelection, setHasConfirmedSelection] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [showNoResultsHint, setShowNoResultsHint] = useState(false);
    const [inputAttempts, setInputAttempts] = useState(0);

    useEffect(() => {
      if (defaultValue) {
        setSelected(defaultValue);
        setHasConfirmedSelection(true);
        setHasUserTyped(false);
      } else {
        setSelected('');
        setHasConfirmedSelection(false);
        setHasUserTyped(false);
      }
      setShowNoResultsHint(false);
      setInputAttempts(0);
    }, [country, defaultValue, disabled]);

    useEffect(() => {
      const query = selected.trim();
      const shouldCheckNoResults = hasUserTyped
        && !hasConfirmedSelection
        && isInputFocused
        && query.length >= 6
        && inputAttempts >= 1;

      if (!shouldCheckNoResults) {
        setShowNoResultsHint(false);
        return;
      }

      const timeoutId = window.setTimeout(() => {
        setShowNoResultsHint(true);
      }, 2500);

      return () => window.clearTimeout(timeoutId);
    }, [selected, hasUserTyped, hasConfirmedSelection, inputAttempts, isInputFocused]);

    const handleSelectPlace = (place) => {
      setHasUserTyped(true);
      setHasConfirmedSelection(false);
      setShowNoResultsHint(false);
      setInputAttempts((prev) => prev + 1);
      setSelected(place);
      onChange?.(place);
    }

    const findComponent = (source, type) =>
      source?.address_components?.find(c => c.types.includes(type))?.long_name || null;

    const findComponentFromOptions = (source, options) =>
      options.reduce((value, option) => value || findComponent(source, option), null);

    const extractData = (source) => ({
        extra: findComponent(source, 'subpremise') || '',
        num: findComponent(source, 'street_number'),
        street: findComponent(source, 'route'),
        postalCode: findComponent(source, 'postal_code'),
        city: findComponentFromOptions(source, ['locality', 'postal_town', 'administrative_area_level_3', 'sublocality']),
        adminArea: findComponent(source, 'administrative_area_level_1'),
        adminArea2: findComponentFromOptions(source, ['administrative_area_level_2', 'administrative_area_level_3', 'locality']),
        countryName: findComponent(source, 'country'),
    });

    const buildSelectedAddress = (place, address, { extra, num, street }) => {
      const isEstablishment = place.types?.some((type) =>
        ['establishment', 'point_of_interest', 'car_dealer', 'car_repair', 'car_rental'].includes(type)
      );
      const fromComponents = [street, num].filter(Boolean).join(', ') + (extra ? ` ${extra}` : '');

      if (fromComponents.trim().length >= 3) {
        return isEstablishment && street && num
          ? `${street}, ${num} ${extra}`.trim()
          : fromComponents.trim();
      }

      return (place.formatted_address || address).trim();
    };

    const handleSelect = async (address, placeId) => {
      if(errors?.direccion) clearErrors('direccion')
      const [place] = await geocodeByPlaceId(placeId);

      let data = extractData(place);

      if (Object.values(data).some((value) => value === null || value === '')) {
        const [results] = await geocodeByAddress(address);
        const fallbackData = extractData(results);

        data = {
            ...data,
            num: data.num || fallbackData.num,
            street: data.street || fallbackData.street,
            postalCode: data.postalCode || fallbackData.postalCode,
            city: data.city || fallbackData.city,
            adminArea: data.adminArea || fallbackData.adminArea,
            adminArea2: data.adminArea2 || fallbackData.adminArea2,
            countryName: data.countryName || fallbackData.countryName,
        };
      }

      const { extra, num, street, postalCode, city, adminArea, adminArea2, countryName } = data;
      const selectedAddress = buildSelectedAddress(place, address, { extra, num, street });

      if (!postalCode && !city) {
        toast.warning(t('errors.google-places.street'));
        return;
      }

      if (!selectedAddress || selectedAddress.length < 3) {
        toast.warning(t('errors.google-places.street'));
        return;
      }

      setSelected(selectedAddress)
      setHasConfirmedSelection(true);
      setHasUserTyped(false);
      setShowNoResultsHint(false);
      setInputAttempts(0);

      const poblacion = city || adminArea2 || '';
      const area = [adminArea2, adminArea].filter(Boolean).join(', ')
        || [city, adminArea].filter(Boolean).join(', ')
        || countryName
        || '';

      onSelectResult({
        direccion: selectedAddress,
        codigo_postal: postalCode || '',
        poblacion,
        area,
      })
    };

    const handleOnBlur = () => {
      setIsInputFocused(false);
      setShowNoResultsHint(false);

      if (selected.trim()) {
        onChange?.(selected.trim());
        clearErrors('direccion');
      }
    }

    const getDireccionErrorMessage = () => t('carrito.envio.form.errors.direccion');

    return (
        <PlacesAutocomplete
          value={selected}
          searchOptions={{
            types: ['street_address', 'route', 'car_dealer', 'car_rental', 'car_repair'],
            language: 'es',
            componentRestrictions: {
              country: [country],
            }
          }}
          onChange={handleSelectPlace}
          onSelect={handleSelect}
          onError={(status, clearSuggestions) => {
            if(status === 'ZERO_RESULTS') {
              toast.warning(t('errors.google-places.no-results'));
            } else {
              toast.error(t('errors.google-places.api-error'));
            }

            clearSuggestions()
          }}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
            <div className="places-search">
              <label className="places-search__label" htmlFor="dir-direccion">
                {t('carrito.envio.form.direccion')} *
              </label>
              <input
                id="dir-direccion"
                {...getInputProps({
                  disabled: disabled,
                  placeholder: t('carrito.envio.form.google_places_placeholder'),
                  autoComplete: 'new-password',
                  onFocus: () => setIsInputFocused(true),
                  onBlur: handleOnBlur,
                })}
              />
              <div className="places-search-container">
                {showNoResultsHint && (
                  <div className="places-search__hint">
                    {t('carrito.envio.form.google_places_hint')}
                  </div>
                )}
                {loading && <div className="suggestion-item">Loading...</div>}
                {suggestions.map((suggestion, i) => {
                  const className = suggestion.active
                    ? 'suggestion-item-active'
                    : 'suggestion-item';
                  const style = suggestion.active
                    ? { backgroundColor: '#fafafa', cursor: 'pointer' }
                    : { backgroundColor: '#ffffff', cursor: 'pointer' };
                  return (
                    <div
                      {...getSuggestionItemProps(suggestion, {
                        className,
                        style,
                      })}
                      key={'place-suggestion-' + i}
                    >
                      <span>{suggestion.description}</span>
                    </div>
                  );
                })}
              </div>
              {errors?.direccion && (
                <span role="alert">
                  <FontAwesomeIcon icon={faCircleXmark} /> {getDireccionErrorMessage()}
                </span>
              )}
            </div>
          )}
        </PlacesAutocomplete>
      );
}
